/**
 * Model Editor Module
 * 
 * Form for creating and editing model configurations (Admin only).
 */

import logger from '../../core/logger.js';
import { modelsService } from '../../services/models-service.js';
import { ModelConfig, ModelProvider } from '../../types/prompt-model.js';
import toast from '../../ui/toast.js';

/**
 * Model editor callbacks
 */
interface ModelEditorCallbacks {
  onSave: () => void;
  onCancel: () => void;
}

let callbacks: ModelEditorCallbacks | null = null;

/**
 * Register callbacks
 */
export function registerModelEditorCallbacks(cbs: ModelEditorCallbacks): void {
  callbacks = cbs;
}

/**
 * Render model editor (create or edit mode)
 */
export async function renderModelEditor(
  container: HTMLElement,
  modelId?: string
): Promise<void> {
  logger.info('admin: Rendering model editor', { modelId });
  
  // Clear container
  container.innerHTML = '';
  
  // Load existing model if editing
  let model: ModelConfig | null = null;
  if (modelId) {
    try {
      model = await modelsService.getModel(modelId);
    } catch (error) {
      logger.error('admin: Failed to load model', { modelId, error });
      toast.error('Failed to load model');
      return;
    }
  }
  
  const isEditMode = !!model;
  
  // Create header
  const header = document.createElement('div');
  header.className = 'admin-header';
  
  const heading = document.createElement('h1');
  heading.className = 'admin-heading';
  heading.textContent = isEditMode ? 'Edit Model Configuration' : 'Add New Model';
  header.appendChild(heading);
  
  const subheading = document.createElement('p');
  subheading.className = 'admin-subheading';
  subheading.textContent = isEditMode 
    ? `Editing: ${model!.id}`
    : 'Add a new LLM model to the allowlist';
  header.appendChild(subheading);
  
  container.appendChild(header);
  
  // Create form
  const form = document.createElement('form');
  form.className = 'model-editor-form';
  form.style.maxWidth = '800px';
  form.style.margin = '0 auto';
  
  // Model ID (read-only if editing)
  if (isEditMode) {
    const idGroup = document.createElement('div');
    idGroup.className = 'form-group';
    
    const idLabel = document.createElement('label');
    idLabel.className = 'form-label';
    idLabel.textContent = 'Model ID';
    idGroup.appendChild(idLabel);
    
    const idValue = document.createElement('div');
    idValue.style.padding = '0.5rem';
    idValue.style.backgroundColor = '#f3f4f6';
    idValue.style.borderRadius = '0.375rem';
    idValue.style.fontFamily = 'monospace';
    idValue.textContent = model!.id;
    idGroup.appendChild(idValue);
    
    form.appendChild(idGroup);
  } else {
    const idGroup = createFormGroup(
      'Model ID',
      'id',
      'text',
      '',
      'e.g., gemini:gemini-2.5-flash or openai:gpt-4o-mini'
    );
    const hint = document.createElement('div');
    hint.style.fontSize = '0.875rem';
    hint.style.color = '#6b7280';
    hint.style.marginTop = '0.25rem';
    hint.textContent = 'Format: provider:modelName (cannot be changed after creation)';
    idGroup.appendChild(hint);
    form.appendChild(idGroup);
  }
  
  // Provider (read-only if editing)
  if (isEditMode) {
    const providerGroup = document.createElement('div');
    providerGroup.className = 'form-group';
    
    const providerLabel = document.createElement('label');
    providerLabel.className = 'form-label';
    providerLabel.textContent = 'Provider';
    providerGroup.appendChild(providerLabel);
    
    const providerValue = document.createElement('div');
    providerValue.style.padding = '0.5rem';
    providerValue.style.backgroundColor = '#f3f4f6';
    providerValue.style.borderRadius = '0.375rem';
    providerValue.textContent = model!.provider.toUpperCase();
    providerGroup.appendChild(providerValue);
    
    form.appendChild(providerGroup);
  } else {
    const providerGroup = createSelectGroup(
      'Provider',
      'provider',
      [
        { value: ModelProvider.Gemini, label: 'Gemini' },
        { value: ModelProvider.OpenAI, label: 'OpenAI' }
      ],
      ModelProvider.Gemini
    );
    form.appendChild(providerGroup);
  }
  
  // Temperature
  const temperatureGroup = createFormGroup(
    'Temperature',
    'temperature',
    'number',
    model?.parameters.temperature?.toString() || '0.7',
    '0.0 - 2.0'
  );
  const tempInput = temperatureGroup.querySelector('input') as HTMLInputElement;
  tempInput.step = '0.1';
  tempInput.min = '0';
  tempInput.max = '2';
  form.appendChild(temperatureGroup);
  
  // Max Tokens
  const maxTokensGroup = createFormGroup(
    'Max Tokens',
    'maxTokens',
    'number',
    model?.parameters.maxTokens?.toString() || '2000',
    'Maximum output length'
  );
  const tokensInput = maxTokensGroup.querySelector('input') as HTMLInputElement;
  tokensInput.min = '1';
  tokensInput.step = '1';
  form.appendChild(maxTokensGroup);
  
  // Cost per 1k tokens
  const costGroup = createFormGroup(
    'Estimated Cost per 1k Tokens (USD)',
    'cost',
    'number',
    model?.estimated_cost_per_1k_tokens?.toString() || '',
    'e.g., 0.0001'
  );
  const costInput = costGroup.querySelector('input') as HTMLInputElement;
  costInput.step = '0.000001';
  costInput.min = '0';
  form.appendChild(costGroup);
  
  // Enabled checkbox
  const enabledGroup = document.createElement('div');
  enabledGroup.className = 'form-group';
  
  const enabledLabel = document.createElement('label');
  enabledLabel.style.display = 'flex';
  enabledLabel.style.alignItems = 'center';
  enabledLabel.style.gap = '0.5rem';
  
  const enabledCheckbox = document.createElement('input');
  enabledCheckbox.type = 'checkbox';
  enabledCheckbox.name = 'enabled';
  enabledCheckbox.checked = model?.enabled ?? true;
  enabledLabel.appendChild(enabledCheckbox);
  
  const enabledText = document.createElement('span');
  enabledText.textContent = 'Enable this model for use';
  enabledLabel.appendChild(enabledText);
  
  enabledGroup.appendChild(enabledLabel);
  form.appendChild(enabledGroup);
  
  // Action buttons
  const actionsDiv = document.createElement('div');
  actionsDiv.className = 'form-actions';
  actionsDiv.style.display = 'flex';
  actionsDiv.style.gap = '1rem';
  actionsDiv.style.marginTop = '2rem';
  
  const saveBtn = document.createElement('button');
  saveBtn.type = 'button';
  saveBtn.className = 'btn btn--primary';
  saveBtn.textContent = isEditMode ? 'Save Changes' : 'Add Model';
  saveBtn.addEventListener('click', async () => {
    await handleSave(form, modelId);
  });
  actionsDiv.appendChild(saveBtn);
  
  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.className = 'btn';
  cancelBtn.textContent = 'Cancel';
  cancelBtn.addEventListener('click', () => {
    if (callbacks) callbacks.onCancel();
  });
  actionsDiv.appendChild(cancelBtn);
  
  form.appendChild(actionsDiv);
  
  container.appendChild(form);
}

/**
 * Create a form group with label and input
 */
function createFormGroup(
  label: string,
  name: string,
  type: string,
  value: string,
  placeholder?: string
): HTMLElement {
  const group = document.createElement('div');
  group.className = 'form-group';
  
  const labelEl = document.createElement('label');
  labelEl.className = 'form-label';
  labelEl.textContent = label;
  labelEl.htmlFor = `model-${name}`;
  group.appendChild(labelEl);
  
  const input = document.createElement('input');
  input.type = type;
  input.id = `model-${name}`;
  input.name = name;
  input.className = 'form-input';
  input.value = value;
  if (placeholder) input.placeholder = placeholder;
  group.appendChild(input);
  
  return group;
}

/**
 * Create a select group with label and dropdown
 */
function createSelectGroup(
  label: string,
  name: string,
  options: Array<{ value: string; label: string }>,
  value: string
): HTMLElement {
  const group = document.createElement('div');
  group.className = 'form-group';
  
  const labelEl = document.createElement('label');
  labelEl.className = 'form-label';
  labelEl.textContent = label;
  labelEl.htmlFor = `model-${name}`;
  group.appendChild(labelEl);
  
  const select = document.createElement('select');
  select.id = `model-${name}`;
  select.name = name;
  select.className = 'form-input';
  
  options.forEach(opt => {
    const option = document.createElement('option');
    option.value = opt.value;
    option.textContent = opt.label;
    if (opt.value === value) option.selected = true;
    select.appendChild(option);
  });
  
  group.appendChild(select);
  return group;
}

/**
 * Handle save action
 */
async function handleSave(form: HTMLFormElement, modelId?: string): Promise<void> {
  try {
    // Gather form data
    const formData = new FormData(form);
    
    if (modelId) {
      // Update existing model
      const temperature = parseFloat(formData.get('temperature') as string);
      const maxTokens = parseInt(formData.get('maxTokens') as string, 10);
      const costRaw = formData.get('cost') as string;
      const estimated_cost_per_1k_tokens = costRaw ? parseFloat(costRaw) : undefined;
      const enabled = formData.get('enabled') === 'on';
      
      await modelsService.updateModel(modelId, {
        parameters: { temperature, maxTokens },
        estimated_cost_per_1k_tokens,
        enabled
      });
      
      toast.success('Model updated successfully');
    } else {
      // Create new model
      const id = formData.get('id') as string;
      const provider = formData.get('provider') as ModelProvider;
      const temperature = parseFloat(formData.get('temperature') as string);
      const maxTokens = parseInt(formData.get('maxTokens') as string, 10);
      const costRaw = formData.get('cost') as string;
      const estimated_cost_per_1k_tokens = costRaw ? parseFloat(costRaw) : undefined;
      const enabled = formData.get('enabled') === 'on';
      
      // Validate
      if (!id || !provider) {
        toast.error('Model ID and provider are required');
        return;
      }
      
      await modelsService.createModel({
        id,
        provider,
        parameters: { temperature, maxTokens },
        estimated_cost_per_1k_tokens,
        enabled
      });
      
      toast.success('Model added successfully');
    }
    
    // Callback to return to list
    if (callbacks) callbacks.onSave();
    
  } catch (error) {
    logger.error('admin: Failed to save model', { error });
    toast.error('Failed to save model');
  }
}
